import { type HTMLAttributes } from 'react';
import {
  AlertStatusIcon,
  CheckStatusIcon,
  ReceiptIcon,
  TrashIcon,
} from '@bravemove/design-tokens/icons';
import { cn } from '../../utils/cn';
import { StatusChip } from '../StatusChip/StatusChip';

export type DocumentTileStatus = 'todo' | 'uploading' | 'done' | 'flagged';
export type DocumentTileFlag = 'mismatch' | 'low_res' | 'unreadable';

export interface DocumentTileProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /** Image data URL, blob URL, or remote URL to render in the 96x96 thumbnail. */
  readonly preview?: string | undefined;
  readonly filename: string;
  readonly sizeBytes?: number | undefined;
  readonly status: DocumentTileStatus;
  /** 0–100, only consulted when `status === 'uploading'`. */
  readonly progress?: number | undefined;
  readonly flag?: DocumentTileFlag | undefined;
  readonly onView?: (() => void) | undefined;
  readonly onRemove?: (() => void) | undefined;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const STATUS_TILE: Record<DocumentTileStatus, { border: string; thumb: string; ink: string }> = {
  todo: { border: 'border-hairline-2', thumb: 'bg-surface-2 text-ink-3', ink: 'text-ink-3' },
  uploading: { border: 'border-accent', thumb: 'bg-accent/10 text-accent', ink: 'text-ink-2' },
  done: { border: 'border-success/60', thumb: 'bg-success/10 text-success', ink: 'text-ink' },
  flagged: { border: 'border-warning', thumb: 'bg-warning/10 text-warning', ink: 'text-ink' },
};

/**
 * Document tile — used by F09 in both the consumer upload step and the admin
 * review detail. Renders a 96×96 thumbnail, filename, size, status chip and
 * (optionally) a progress bar for uploads and a remove control.
 */
export function DocumentTile({
  preview,
  filename,
  sizeBytes,
  status,
  progress,
  flag,
  onView,
  onRemove,
  className,
  ...rest
}: DocumentTileProps) {
  const sty = STATUS_TILE[status];
  return (
    <div
      className={cn(
        'inline-flex items-start gap-3 p-3 rounded-lg border bg-surface w-full max-w-md',
        sty.border,
        className,
      )}
      {...rest}
    >
      <button
        type="button"
        onClick={onView}
        disabled={!onView}
        aria-label={onView ? `View ${filename}` : filename}
        className={cn(
          'shrink-0 size-24 rounded-md overflow-hidden inline-flex items-center justify-center',
          sty.thumb,
          onView ? 'cursor-pointer hover:opacity-90' : 'cursor-default',
        )}
      >
        {preview ? (
          <img src={preview} alt={filename} className="size-full object-cover" loading="lazy" />
        ) : (
          <ReceiptIcon size={36} color="currentColor" />
        )}
      </button>

      <div className="min-w-0 flex-1 flex flex-col gap-1.5">
        <p className={cn('text-[13px] font-semibold font-mono truncate', sty.ink)} title={filename}>
          {filename}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {sizeBytes !== undefined ? (
            <span className="text-[11px] text-ink-3 font-mono tabular-nums">
              {formatBytes(sizeBytes)}
            </span>
          ) : null}
          <StatusChip
            size="sm"
            status={
              status === 'done'
                ? 'approved'
                : status === 'flagged'
                  ? 'expiring'
                  : status === 'uploading'
                    ? 'in_review'
                    : 'pending'
            }
            label={
              status === 'done'
                ? 'Done'
                : status === 'uploading'
                  ? 'Uploading'
                  : status === 'flagged'
                    ? (flag ?? 'flagged').replace('_', ' ')
                    : 'To do'
            }
            pulse={status === 'uploading'}
          />
          {status === 'flagged' && flag ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-warning">
              <AlertStatusIcon size={12} color="currentColor" />
              <span className="capitalize">{flag.replace('_', ' ')}</span>
            </span>
          ) : null}
          {status === 'done' && !flag ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-success">
              <CheckStatusIcon size={12} color="currentColor" />
              Verified
            </span>
          ) : null}
        </div>

        {status === 'uploading' && progress !== undefined ? (
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            className="h-1.5 w-full rounded-full bg-surface-2 overflow-hidden mt-1"
          >
            <div
              className="h-full bg-accent transition-[width] duration-200"
              style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
            />
          </div>
        ) : null}

        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${filename}`}
            className="self-start inline-flex items-center gap-1 text-[11.5px] text-danger hover:underline underline-offset-2"
          >
            <TrashIcon size={12} color="currentColor" />
            <span>Remove</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default DocumentTile;
